import styled from 'styled-components';
import { Card, SecondaryButton, PrimaryButton, Spinner, color } from '../App.styles';
import { CONTENT_LABEL, ACTION_LABEL, layoutInfoRows } from '../minutes';

function ResultPanel({
  status,
  minutes,
  fields,
  previewFields,
  error,
  templateName,
  isAudio,
  loadingMessage,
  copied,
  downloading,
  onCopy,
  onDownload,
  onRetry,
  onDismiss,
}) {
  return (
    <Panel>
      <PanelHeader>
        <div>
          <h2>정리 결과</h2>
          <small>템플릿 : {templateName}</small>
        </div>
        {status === 'done' && (
          <Actions>
            <SecondaryButton onClick={onCopy}>{copied ? '복사됨' : '복사'}</SecondaryButton>
            <SecondaryButton onClick={onDownload} disabled={downloading}>
              {downloading ? '만드는 중...' : 'Word 다운로드'}
            </SecondaryButton>
          </Actions>
        )}
      </PanelHeader>

      {status === 'idle' && (
        <Empty>
          <p>정리된 회의록이 여기에 표시됩니다.</p>
          <ul>
            {[...previewFields.map(({ label }) => label), CONTENT_LABEL, ACTION_LABEL].map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        </Empty>
      )}

      {status === 'loading' && (
        <StateBox>
          <Spinner />
          <strong>{loadingMessage || 'AI가 회의록을 정리하고 있습니다...'}</strong>
          {isAudio && <span>음성 파일은 시간이 더 걸릴 수 있습니다. 이 화면을 닫지 말아 주세요.</span>}
        </StateBox>
      )}

      {status === 'error' && (
        <StateBox>
          <strong>오류가 발생했습니다</strong>
          <span>{error || '잠시 후 다시 시도해 주세요.'}</span>
          <span>입력한 내용은 그대로 유지됩니다.</span>
          <ErrorActions>
            <SecondaryButton onClick={onDismiss}>확인</SecondaryButton>
            <PrimaryButton onClick={onRetry}>다시 시도</PrimaryButton>
          </ErrorActions>
        </StateBox>
      )}

      {status === 'done' && minutes && (
        <Document>
          {fields.length > 0 && (
            <>
              <h3>회의 정보</h3>
              <InfoTable>
                <tbody>
                  {layoutInfoRows(fields).map((row) => (
                    <tr key={row.map(({ key }) => key).join()}>
                      {row.map(({ key, label }) => [
                        <th key={`${key}-label`}>{label}</th>,
                        <td key={key} colSpan={row.length === 1 ? 3 : 1}>
                          {minutes.info[key] || '-'}
                        </td>,
                      ])}
                    </tr>
                  ))}
                </tbody>
              </InfoTable>
            </>
          )}

          <h3>회의 내용</h3>
          {minutes.sections.map((section, index) => (
            <Section key={index}>
              <h4>
                {index + 1}. {section.title}
              </h4>
              <ul>
                {section.items.map((item, itemIndex) => (
                  <li key={itemIndex}>{item}</li>
                ))}
              </ul>
            </Section>
          ))}

          {minutes.actionItems.length > 0 && (
            <>
              <h3>액션아이템</h3>
              <ActionTable>
                <thead>
                  <tr>
                    <th>담당자</th>
                    <th>할 일</th>
                    <th>기한</th>
                  </tr>
                </thead>
                <tbody>
                  {minutes.actionItems.map((item, index) => (
                    <tr key={index}>
                      <td>{item.owner}</td>
                      <td>{item.task}</td>
                      <td>{item.due}</td>
                    </tr>
                  ))}
                </tbody>
              </ActionTable>
            </>
          )}
        </Document>
      )}
    </Panel>
  );
}

const Panel = styled(Card)`
  position: sticky;
  top: 24px;
  min-height: 560px;

  @media (max-width: 960px) {
    position: static;
    min-height: 0;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${color.borderLight};

  h2 {
    font-size: 17px;
    font-weight: 700;
  }

  small {
    font-size: 13px;
    color: ${color.textMuted};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Empty = styled.div`
  padding: 48px 0;
  text-align: center;
  color: ${color.textMuted};

  ul {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
    margin-top: 16px;
  }

  li {
    padding: 4px 10px;
    background-color: ${color.bgSoft};
    border: 1px solid ${color.borderLight};
    border-radius: 999px;
    font-size: 12px;
  }
`;

const StateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 80px 16px;
  text-align: center;

  strong {
    font-size: 16px;
  }

  span {
    font-size: 14px;
    color: ${color.textMuted};
  }
`;

const ErrorActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;

  button {
    width: auto;
    height: 36px;
    padding: 0 16px;
    font-size: 14px;
  }
`;

const Document = styled.div`
  padding-top: 8px;

  h3 {
    margin: 20px 0 10px;
    font-size: 15px;
    font-weight: 700;
    color: ${color.primary};
  }
`;

const tableStyle = `
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th,
  td {
    padding: 8px 10px;
    border: 1px solid ${color.borderLight};
    text-align: left;
    vertical-align: top;
    line-height: 1.5;
  }

  th {
    background-color: ${color.bg};
    font-weight: 500;
    color: ${color.textSub};
    white-space: nowrap;
  }
`;

const InfoTable = styled.table`
  ${tableStyle}

  th {
    width: 15%;
  }
`;

const ActionTable = styled.table`
  ${tableStyle}
`;

const Section = styled.div`
  & + & {
    margin-top: 12px;
  }

  h4 {
    font-size: 14px;
    font-weight: 700;
  }

  ul {
    margin-top: 6px;
    padding-left: 4px;
  }

  li {
    position: relative;
    padding-left: 12px;
    font-size: 14px;
    line-height: 1.7;
    color: ${color.textSub};

    &::before {
      content: '';
      position: absolute;
      top: 10px;
      left: 0;
      width: 4px;
      height: 4px;
      background-color: ${color.textMuted};
      border-radius: 50%;
    }
  }
`;

export default ResultPanel;
